import { useEffect, useState } from "react";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";

/**
 * Returns true when the current viewer is permitted to see synthetic/demo
 * matches and QA-only surfaces. Two ways to opt in:
 *  - The user is a verified superadmin/admin (server-side RBAC).
 *  - The URL carries an explicit `?mode=demo` query flag AND the viewer is a
 *    superadmin. The query param alone is NOT sufficient — a normal signed-in
 *    user appending `?mode=demo` to the URL must not see synthetic data.
 *
 * Notes:
 *  - `?demo=1` is intentionally NOT honoured here. That flag is reserved for
 *    the auth-bypass shortcut used by /welcome -> "/" and must keep its
 *    existing meaning. Demo data exposure is a separate, narrower decision.
 *  - Regular signed-in users get `false` — they should never see synthetic
 *    matches presented as real fixtures, even with `?mode=demo` in the URL.
 *  - Broader admin/QA role gating is a follow-up: only the superadmin role
 *    helper exists today, so that is the sole privileged check used here.
 */
export function useDemoModeAllowed(): boolean {
  const { isSuperAdmin } = useSuperAdmin();
  const [urlOptIn, setUrlOptIn] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return new URLSearchParams(window.location.search).get("mode") === "demo";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const recheck = () => {
      try {
        setUrlOptIn(
          new URLSearchParams(window.location.search).get("mode") === "demo",
        );
      } catch {
        setUrlOptIn(false);
      }
    };
    window.addEventListener("popstate", recheck);
    return () => window.removeEventListener("popstate", recheck);
  }, []);
  // Superadmin always sees demo. `?mode=demo` is a no-op for normal users —
  // it only acts as an explicit opt-in for an already-privileged superadmin.
  if (isSuperAdmin) return true;
  void urlOptIn; // intentionally ignored without privileged role
  return false;
}